import { useCallback, useContext, useMemo, useState } from "react";
import { LinkButton } from "../common/buttons/LinkButton/LinkButton";
import { LucideIcon } from "../common/icon/LucideIcon";
import { SessionContext } from "../common/contexts/SessionContext";
import { getIndexRoute } from "../main/Routing";
import { FileExportJobFilters, fileExportJobsConstants } from "../api/FileExportJobs";
import { useEntityCount } from "../api/BaseEntityApi";
import styles from "./FileExportJobsWidget.module.css";
import { CircularProgress } from "../common/loaders/CircularProgress/CircularProgress";
import { LiveConnectionCallbacks, useListen } from "../common/signalr/SignalRContext";

export const FileExportJobsWidget = () => {
  const { data: notDownloadedCountData, refetch: notDownloadedCountDataRefetch } = useEntityCount<FileExportJobFilters>(
    fileExportJobsConstants.resource,
    {
      isDownloaded: false,
    }
  );

  const { route, session } = useContext(SessionContext);

  const [stateDict, setStateDict] = useState<Record<number, number> | undefined>(undefined);
  const combinedProgress = useMemo(() => {
    if (!stateDict) return undefined;
    const totalPercent = Object.keys(stateDict).length * 100;
    const completed = Object.values(stateDict).reduce((acc, progress) => acc + progress, 0);
    const progress = totalPercent > 0 ? (completed / totalPercent) * 100 : 0;
    if (progress >= 100) setStateDict(undefined); // Reset state if all jobs are completed
    return progress;
  }, [stateDict]);

  const callback: LiveConnectionCallbacks["FileExportJobProgressChanged"] = useCallback(
    (jobId, userId, progress) => {
      if (!!session?.userId && session?.userId === userId) {
        notDownloadedCountDataRefetch();
        setStateDict((prev) => ({ ...prev, [jobId]: progress }));
      }
    },
    [session?.userId, notDownloadedCountDataRefetch]
  );
  useListen("FileExportJobProgressChanged", callback);

  if (window.location.pathname.includes(fileExportJobsConstants.frontendIndexRoute)) return null; // Don't show the widget on the file export jobs page
  const notDownloadedCount = notDownloadedCountData?.count ?? 0;
  if (notDownloadedCount === 0) return null;
  return (
    <LinkButton
      className="btn btn-ghost-secondary btn-round"
      linkTo={route(getIndexRoute(fileExportJobsConstants.frontendIndexRoute))}
      linkStyle={{ padding: 0, margin: 0 }}
      style={{ padding: "7px 10.5px", margin: 0, marginRight: "5px", position: "relative" }}
      onClick={(e) => e.stopPropagation()}
      target="_blank"
    >
      <div
        className={
          !!combinedProgress && combinedProgress !== 100
            ? styles.FileExportJobsWidgetIconActiveDownload
            : styles.FileExportJobsWidgetIcon
        }
      >
        <LucideIcon name="download" />
      </div>
      {!!combinedProgress && combinedProgress !== 100 ? (
        <div
          className={`${styles.FileExportJobsWidgetCount}`}
          style={{
            position: "absolute",
            top: "-3px",
            right: "-6px",
            borderRadius: "0.25em",
            fontSize: "9px",
            padding: "3px",
            fontWeight: 600,
          }}
        >
          <CircularProgress width={16} progress={combinedProgress} />
        </div>
      ) : (
        <div
          className={`label label-primary ${styles.FileExportJobsWidgetCount}`}
          style={{
            position: "absolute",
            top: "-1px",
            right: "-1px",
            borderRadius: "0.25em",
            fontSize: "9px",
            padding: "3px",
            boxShadow: "0 1px 3px rgba(25, 25, 25, 0.3)",
            fontWeight: 600,
          }}
        >
          {notDownloadedCount}
        </div>
      )}
    </LinkButton>
  );
};
